import { useReducer, useRef } from "react";
import { useChargebeeCheckout } from "../util/chargebee";
import useShortCode from "./useShortCode";
import useSubscription from "./useSubscription";
import { BillingProvider } from "../util/constants";
import usePush from "./usePush";
import BillingError from "../util/BillingError";
import { errors } from "@chaser/codes";
import { useCacheActions } from "./useFetch";
import OrganisationService from "../services/organisation-service";
import ManageService from "../services/manage-service";
import { invalidateEndpoint } from "./useFetch/cacheActions";
import SubscriptionService from "../services/subscription-service";
import { useSubscriptionTransition } from "../contexts/SubscriptionTransitionContext";

function useChargebee({
  planName,
  shortCode,
  currencyCode,
  period,
  moduleName,
  dispatch,
  throwOnError = true,
  onSuccess = () => {},
}) {
  const cbSuccess = useRef(false);
  const { invalidateDomainAndWait } = useCacheActions();
  const { startTransition, endTransition } = useSubscriptionTransition();
  const { execute: stopChasing } = usePush(OrganisationService.pauseChasing);
  const { execute: persistModuleSubscriptionRecord } = usePush(
    ManageService.persistModuleSubscriptionRecord,
  );
  const [, breakPage] = useReducer((_, e) => {
    if (throwOnError) {
      throw new BillingError(e, {
        provider: BillingProvider.CHARGEBEE,
        code: errors.CHARGEBEE_BILLING_ERROR,
      });
    }
  });

  const { clickCallback: chargebeeClickCallback } = useChargebeeCheckout({
    organisationShortCode: shortCode,
    planName,
    currency: currencyCode,
    period,
    moduleName,
    chargebeeOptions: {
      loaded() {
        cbSuccess.current = false;
      },
      success() {
        cbSuccess.current = true;
        invalidateEndpoint(SubscriptionService.getAvailableAddons);
      },
      error(e) {
        breakPage(e);
      },
      async close() {
        if (cbSuccess.current) {
          startTransition();

          try {
            const [persistError, persisted] =
              await persistModuleSubscriptionRecord(shortCode, {
                planName,
                currency: currencyCode,
                period,
              });

            if (!persistError && persisted === false) {
              await new Promise((resolve) => {
                setTimeout(resolve, 1500);
              });

              await persistModuleSubscriptionRecord(shortCode, {
                planName,
                currency: currencyCode,
                period,
              });
            }

            onSuccess();

            dispatch({
              type: "setSuccess",
              payload:
                "Success! It may take few minutes for the changes to be visible in Chaser.",
            });

            await stopChasing(shortCode);
            await invalidateDomainAndWait("organisation");
          } finally {
            endTransition();
          }
        }
      },
    },
  });

  return chargebeeClickCallback;
}

function useSubscribe(
  dispatch,
  { planName, currencyCode, period, moduleName },
  onSuccess = () => {},
) {
  const shortCode = useShortCode();

  const chargebeeCheckout = useChargebee({
    planName,
    shortCode,
    currencyCode,
    period,
    moduleName,
    dispatch,
    onSuccess,
    throwOnError: false,
  });
  const { execute: showChargebeeCheckout } = usePush(chargebeeCheckout);

  return showChargebeeCheckout;
}

export default useSubscribe;
